import { type Epic } from 'redux-observable';
import {
  filter,
  mergeMap,
  map,
  catchError,
  of,
  merge,
  forkJoin,
  from,
  withLatestFrom,
  type Observable,
  EMPTY,
} from 'rxjs';
import { openSnackbar } from '@/core/features/snackbar';
import {
  fetchAmenities,
  fetchAreas,
  fetchPropertyTypes,
  fetchSeasons,
  type Amenity,
  type Area,
  type PropertyType,
  type Season,
} from '@/api/istaff/referenceData';
import i18n from '@/i18n/i18n';
import type { RootState } from '@/state/store';
import {
  selectReferenceDataLoaded,
  selectReferenceDataLoading,
  selectReferenceDataLocale,
} from '../selectors';
import {
  fetchReferenceData,
  fetchReferenceDataFailed,
  setReferenceData,
  setReferenceDataLoading,
  setReferenceDataFailed,
} from '../actions';

const referenceDataEpic: Epic = (action$, state$) =>
  action$.pipe(
    filter(fetchReferenceData.match),
    withLatestFrom(state$ as Observable<RootState>),
    mergeMap(([, state]) => {
      const locale = (i18n.language ?? i18n.resolvedLanguage ?? 'en').split(
        '-',
      )[0];

      if (
        selectReferenceDataLoading(state) ||
        (selectReferenceDataLoaded(state) &&
          selectReferenceDataLocale(state) === locale)
      ) {
        return EMPTY;
      }

      return merge(
        of(setReferenceDataLoading()),
        forkJoin({
          amenities: from(fetchAmenities()).pipe(
            catchError(() => of({ data: [] as Amenity[] })),
          ),
          areas: from(fetchAreas()).pipe(
            catchError(() => of({ data: [] as Area[] })),
          ),
          propertyTypes: from(fetchPropertyTypes()).pipe(
            catchError(() => of({ data: [] as PropertyType[] })),
          ),
          seasons: from(fetchSeasons()).pipe(
            catchError(() => of({ data: [] as Season[] })),
          ),
        }).pipe(
          map(({ amenities, areas, propertyTypes, seasons }) =>
            setReferenceData({
              amenities: amenities.data,
              areas: areas.data,
              propertyTypes: propertyTypes.data,
              seasons: seasons.data,
              locale,
            }),
          ),
          catchError(() =>
            of(
              setReferenceDataFailed(),
              fetchReferenceDataFailed(),
              openSnackbar({
                snackbarType: 'error',
                snackbarMessage:
                  'Failed to load reference data. Please refresh.',
              }),
            ),
          ),
        ),
      );
    }),
  );

export default referenceDataEpic;
